/**
 * Screen 14h — business configuration.
 *
 * This is the screen that makes the project's configuration rule true rather
 * than a paragraph in a README. Everything here used to require an `UPDATE`
 * statement, which meant every business change was a developer ticket.
 *
 * Settings the committee has not answered are shown first and marked. A
 * default that nobody chose is not the same thing as a decision, and the
 * difference matters when the app is charging members a fee on the strength
 * of it.
 *
 * A setting this volunteer's role cannot change is rendered locked rather
 * than hidden — a PROGRAM volunteer should be able to see the service fee
 * without being able to move it.
 */
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorNote, Note, Shell } from "../../components/Shell";
import { ApiError, api, type AdminSetting } from "../../lib/api";
import { formatIdr } from "../../lib/format";

export default function AdminSettings() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<AdminSetting[] | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const body = await api.admin.settings();
      setSettings(body.settings);
      setDrafts(
        Object.fromEntries(body.settings.map((s) => [s.key, String(s.value)])),
      );
    } catch {
      setError(t("error.generic"));
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save(setting: AdminSetting, value: string | boolean) {
    setError(null);
    setSaved(null);
    try {
      await api.admin.putSetting(setting.key, value);
      await load();
      setSaved(t("admin.saved"));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("error.generic"));
    }
  }

  if (!settings) {
    return (
      <Shell area="about" title={t("admin.settings")}>
        {error ? <ErrorNote>{error}</ErrorNote> : <Note>{t("common.loading")}</Note>}
      </Shell>
    );
  }

  // Unanswered committee questions first — they are the ones with a
  // consequence attached.
  const ordered = [...settings].sort((a, b) => {
    const pa = a.awaiting_committee && a.on_default ? 0 : 1;
    const pb = b.awaiting_committee && b.on_default ? 0 : 1;
    return pa - pb;
  });

  return (
    <Shell area="about" title={t("admin.settings")}>
      {error && <ErrorNote>{error}</ErrorNote>}
      {saved && <p className="fallback-note">{saved}</p>}

      <ul className="cards">
        {ordered.map((setting) => {
          const pending = setting.awaiting_committee && setting.on_default;
          return (
            <li key={setting.key} className="card">
              {/* A committee member should not have to read snake_case. The
                  key stays underneath as the thing support can refer to. */}
              <h3 className="card__title">
                {t(`setting.${setting.key}`, { defaultValue: setting.key })}
              </h3>
              <p className="card__meta">{setting.description}</p>

              {pending && (
                <p>
                  <span className="state" data-state="HELD">{t("admin.assumed")}</span>
                </p>
              )}

              {setting.value_type === "bool" ? (
                <label className="rolerow">
                  <input
                    type="checkbox"
                    disabled={!setting.editable}
                    checked={setting.value === true}
                    onChange={(e) => void save(setting, e.target.checked)}
                  />
                  <span>{setting.value === true ? t("admin.on") : t("admin.off")}</span>
                </label>
              ) : (
                <div className="stack">
                  <input
                    className="field"
                    disabled={!setting.editable}
                    inputMode={setting.value_type === "int" ? "numeric" : "text"}
                    value={drafts[setting.key] ?? ""}
                    onChange={(e) =>
                      setDrafts({
                        ...drafts,
                        [setting.key]:
                          setting.value_type === "int"
                            ? e.target.value.replace(/\D/g, "")
                            : e.target.value,
                      })
                    }
                  />
                  {setting.value_type === "int" && setting.key.endsWith("_idr") && (
                    <p className="fallback-note money">
                      {formatIdr(Number(drafts[setting.key] || 0))}
                    </p>
                  )}
                  <button
                    type="button"
                    className="btn btn--primary"
                    disabled={
                      !setting.editable || drafts[setting.key] === String(setting.value)
                    }
                    onClick={() => void save(setting, drafts[setting.key])}
                  >
                    {t("common.save")}
                  </button>
                </div>
              )}

              {!setting.editable && (
                <p className="fallback-note">{t("admin.locked_by_role")}</p>
              )}
            </li>
          );
        })}
      </ul>

      <Link className="btn" to="/admin">
        {t("common.back")}
      </Link>
    </Shell>
  );
}
